import type { InstrumentRunPublic, PaginatedSampleProteoformsResponse } from '@/types';

/** What the section shows in place of rows. `ready` means the grid itself is the content. */
export type RunSampleDetailsStatus =
  | 'loading'
  | 'ready'
  | 'allWithheldQcFailure'
  | 'allWithheldQcPending'
  | 'noSamples'
  | 'decodeNotStarted'
  | 'decodePending'
  | 'decodeFailed'
  | 'noResults'
  | 'forbidden'
  | 'notFound'
  | 'failed';

export function statusFromResponse(
  response: Pick<
    PaginatedSampleProteoformsResponse,
    'total' | 'num_samples' | 'num_samples_withheld_qc_failure' | 'num_samples_withheld_qc_pending'
  >,
  hasActiveFilters: boolean,
  decodeStatus: InstrumentRunPublic['decode_status'],
): RunSampleDetailsStatus {
  if (response.total > 0) return 'ready';
  // num_samples excludes withheld samples, so a run whose every result is QC-withheld would otherwise read as empty.
  if (response.num_samples === 0) {
    if (response.num_samples_withheld_qc_failure > 0) return 'allWithheldQcFailure';
    if (response.num_samples_withheld_qc_pending > 0) return 'allWithheldQcPending';
  }
  // Before the filter check: num_samples and decode status ignore filters, so these hold whatever is filtered.
  if (response.num_samples === 0) return 'noSamples';
  if (decodeStatus === 'failed') return 'decodeFailed';
  // Split from pending because the run page stops polling a not_started run once the instrument is done, so it can't promise results will appear.
  if (decodeStatus === 'not_started') return 'decodeNotStarted';
  if (decodeStatus !== 'completed') return 'decodePending';
  // A filter that matches nothing is the grid working, not an empty run — keep the grid so it can be cleared.
  if (hasActiveFilters) return 'ready';
  return 'noResults';
}

export function statusFromError(error: unknown): RunSampleDetailsStatus {
  const status = typeof error === 'object' && error !== null ? (error as { status?: unknown }).status : undefined;
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  return 'failed';
}
